// Design tokens — single source of truth for the canonical (.theme-deepcos) theme.
// HSL color values are stored as raw triplet strings ("258 100% 64%") so they can
// be used inside Tailwind via hsl(var(--token)).

export type HSL = string;

// Fluid sizing primitive — composes into a CSS clamp() expression.
// `preferred` is a CSS intrinsic-sizing unit (vw, vh, %, em, rem, cqw, cqh).
export interface FluidSize {
  min: number;       // px
  preferred: string; // e.g. "10vw", "3vw", "2.5rem"
  max: number;       // px
}

// Validates the `preferred` string. Used by admin UI before save.
export const PREFERRED_UNIT_RE = /^\d+(\.\d+)?(vw|vh|%|em|rem|cqw|cqh)$/i;

export function isValidFluidSize(f: FluidSize): boolean {
  return (
    typeof f.min === "number" &&
    typeof f.max === "number" &&
    f.min > 0 &&
    f.min < f.max &&
    typeof f.preferred === "string" &&
    PREFERRED_UNIT_RE.test(f.preferred.trim())
  );
}

export function toClamp(f: FluidSize): string {
  return `clamp(${f.min}px, ${f.preferred}, ${f.max}px)`;
}

export interface DesignTokens {
  colors: {
    background: HSL;
    foreground: HSL;
    card: HSL;
    cardForeground: HSL;
    popover: HSL;
    popoverForeground: HSL;
    primary: HSL;
    primaryForeground: HSL;
    primarySoft: HSL;
    primaryHover: HSL;
    primaryGlow: HSL;
    secondary: HSL;
    secondaryForeground: HSL;
    muted: HSL;
    mutedForeground: HSL;
    accent: HSL;
    accentForeground: HSL;
    destructive: HSL;
    destructiveForeground: HSL;
    border: HSL;
    input: HSL;
    ring: HSL;
    seabed: HSL;
    seabedElevated: HSL;
    seabedDeep: HSL;
    seabedForeground: HSL;
    seabedMuted: HSL;
    seabedLine: HSL;
    chartNeutral: HSL;
  };
  typography: {
    fontDisplay: string;
    fontSans: string;
    fontMono: string;
    baseSize: number;
    scaleRatio: number;
    weightRegular: number;
    weightMedium: number;
    weightSemibold: number;
    lineHeightTight: number;
    lineHeightNormal: number;
    lineHeightRelaxed: number;
  };
  spacing: {
    base: number;
    section: FluidSize;
    sectionTight: FluidSize;
    padCardCompact: FluidSize;
    padCardStandard: FluidSize;
  };
  radius: {
    base: number;
    card: number;
    shell: number;
    tile: number;
  };
  shadows: {
    card: string;
    cardHover: string;
    elevated: string;
    glow: string;
  };
}

export const DEFAULT_TOKENS: DesignTokens = {
  colors: {
    background: "0 0% 100%",
    foreground: "230 76% 10%",
    card: "0 0% 100%",
    cardForeground: "230 76% 10%",
    popover: "0 0% 100%",
    popoverForeground: "230 76% 10%",
    primary: "258 100% 64%",
    primaryForeground: "0 0% 100%",
    primarySoft: "258 100% 97%",
    primaryHover: "262 71% 57%",
    primaryGlow: "258 100% 74%",
    secondary: "240 20% 98%",
    secondaryForeground: "230 76% 10%",
    muted: "240 20% 98%",
    mutedForeground: "215 16% 38%",
    accent: "240 100% 99%",
    accentForeground: "258 100% 64%",
    destructive: "0 72% 45%",
    destructiveForeground: "0 0% 100%",
    border: "240 10% 92%",
    input: "240 10% 92%",
    ring: "258 100% 64%",
    seabed: "230 76% 10%",
    seabedElevated: "230 60% 14%",
    seabedDeep: "230 80% 6%",
    seabedForeground: "240 16% 95%",
    seabedMuted: "240 10% 70%",
    seabedLine: "240 10% 22%",
    chartNeutral: "220 9% 64%",
  },
  typography: {
    fontDisplay: '"Inter Variable", "Inter", system-ui, -apple-system, sans-serif',
    fontSans: '"Inter Variable", "Inter", system-ui, -apple-system, sans-serif',
    fontMono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
    baseSize: 16,
    scaleRatio: 1.25,
    weightRegular: 400,
    weightMedium: 500,
    weightSemibold: 600,
    lineHeightTight: 1.05,
    lineHeightNormal: 1.5,
    lineHeightRelaxed: 1.75,
  },
  spacing: {
    base: 4,
    section: { min: 80, preferred: "10vw", max: 160 },
    sectionTight: { min: 64, preferred: "7vw", max: 112 },
    padCardCompact: { min: 20, preferred: "2.5vw", max: 28 },
    padCardStandard: { min: 24, preferred: "3vw", max: 40 },
  },
  radius: {
    base: 6,
    card: 12,
    shell: 28,
    tile: 16,
  },
  shadows: {
    card: "0 1px 2px hsl(230 76% 10% / 0.04), 0 8px 32px -12px hsl(230 76% 10% / 0.08)",
    cardHover:
      "0 1px 2px hsl(230 76% 10% / 0.06), 0 18px 48px -16px hsl(230 76% 10% / 0.16), 0 0 0 1px hsl(var(--primary) / 0.06)",
    elevated: "0 1px 2px hsl(230 76% 10% / 0.06), 0 24px 60px -20px hsl(230 76% 10% / 0.18)",
    glow: "0 10px 36px -12px hsl(var(--primary) / 0.45)",
  },
};

const COLOR_VAR_MAP: Array<[keyof DesignTokens["colors"], string]> = [
  ["background", "--background"],
  ["foreground", "--foreground"],
  ["card", "--card"],
  ["cardForeground", "--card-foreground"],
  ["popover", "--popover"],
  ["popoverForeground", "--popover-foreground"],
  ["primary", "--primary"],
  ["primaryForeground", "--primary-foreground"],
  ["primarySoft", "--primary-soft"],
  ["primaryHover", "--primary-hover"],
  ["primaryGlow", "--primary-glow"],
  ["secondary", "--secondary"],
  ["secondaryForeground", "--secondary-foreground"],
  ["muted", "--muted"],
  ["mutedForeground", "--muted-foreground"],
  ["accent", "--accent"],
  ["accentForeground", "--accent-foreground"],
  ["destructive", "--destructive"],
  ["destructiveForeground", "--destructive-foreground"],
  ["border", "--border"],
  ["input", "--input"],
  ["ring", "--ring"],
  ["seabed", "--seabed"],
  ["seabedElevated", "--seabed-elevated"],
  ["seabedDeep", "--seabed-deep"],
  ["seabedForeground", "--seabed-foreground"],
  ["seabedMuted", "--seabed-muted"],
  ["seabedLine", "--seabed-line"],
  ["chartNeutral", "--chart-neutral"],
];

export function applyTokensToDOM(tokens: DesignTokens, root: HTMLElement = document.documentElement) {
  for (const [key, varName] of COLOR_VAR_MAP) {
    root.style.setProperty(varName, tokens.colors[key]);
  }
  root.style.setProperty("--font-display", tokens.typography.fontDisplay);
  root.style.setProperty("--font-sans", tokens.typography.fontSans);
  root.style.setProperty("--font-mono", tokens.typography.fontMono);
  root.style.setProperty("--radius", `${tokens.radius.base / 16}rem`);
  root.style.setProperty("--radius-card", `${tokens.radius.card}px`);
  root.style.setProperty("--radius-shell", `${tokens.radius.shell}px`);
  root.style.setProperty("--radius-tile", `${tokens.radius.tile}px`);
  root.style.setProperty("--space-section", toClamp(tokens.spacing.section));
  root.style.setProperty("--space-section-tight", toClamp(tokens.spacing.sectionTight));
  root.style.setProperty("--pad-card-compact", toClamp(tokens.spacing.padCardCompact));
  root.style.setProperty("--pad-card-standard", toClamp(tokens.spacing.padCardStandard));
  root.style.setProperty("--shadow-card", tokens.shadows.card);
  root.style.setProperty("--shadow-card-hover", tokens.shadows.cardHover);
  root.style.setProperty("--shadow-elevated", tokens.shadows.elevated);
  root.style.setProperty("--shadow-glow", tokens.shadows.glow);
  root.style.fontSize = `${tokens.typography.baseSize}px`;
}

// HSL ↔ HEX conversion. Input "h s% l%".
export function hslStringToHex(hsl: HSL): string {
  const m = hsl.trim().match(/^(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%$/);
  if (!m) return "#000000";
  const h = parseFloat(m[1]);
  const s = parseFloat(m[2]) / 100;
  const l = parseFloat(m[3]) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0, g1 = 0, b1 = 0;
  if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m2 = l - c / 2;
  const toHex = (v: number) => Math.round((v + m2) * 255).toString(16).padStart(2, "0");
  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}

export function hexToHslString(hex: string): HSL {
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return "0 0% 0%";
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function luminanceFromHex(hex: string): number {
  const clean = hex.replace("#", "");
  const channels = [0, 2, 4].map((i) => {
    const v = parseInt(clean.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

export function contrastRatio(hslA: HSL, hslB: HSL): number {
  const la = luminanceFromHex(hslStringToHex(hslA));
  const lb = luminanceFromHex(hslStringToHex(hslB));
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

const STORAGE_KEY = "deepc:design-tokens:v3";

export function loadFromLocalStorage(): DesignTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DesignTokens;
  } catch {
    return null;
  }
}

export function saveToLocalStorage(tokens: DesignTokens) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    /* ignore */
  }
}

export function mergeWithDefaults(partial: Partial<DesignTokens> | null | undefined): DesignTokens {
  if (!partial) return DEFAULT_TOKENS;
  return {
    colors: { ...DEFAULT_TOKENS.colors, ...(partial.colors ?? {}) },
    typography: { ...DEFAULT_TOKENS.typography, ...(partial.typography ?? {}) },
    spacing: { ...DEFAULT_TOKENS.spacing, ...(partial.spacing ?? {}) },
    radius: { ...DEFAULT_TOKENS.radius, ...(partial.radius ?? {}) },
    shadows: { ...DEFAULT_TOKENS.shadows, ...(partial.shadows ?? {}) },
  };
}
